import { motion } from 'framer-motion';
import { 
  Zap, 
  DollarSign, 
  Lock, 
  Users, 
  CheckCircle, 
  TrendingUp 
} from 'lucide-react';

const features = [
  {
    icon: Zap,
    title: 'Fast Turnaround',
    description: 'Time-sensitive matters require swift action. Our streamlined processes ensure rapid delivery without compromising quality.',
    stat: '24-48hr',
    statLabel: 'Average Delivery',
  },
  {
    icon: DollarSign,
    title: 'Cost-Effective Solutions',
    description: 'Reduce overhead costs without sacrificing quality. Our services provide exceptional value compared to in-house alternatives.',
    stat: '40%',
    statLabel: 'Cost Savings',
  },
  {
    icon: Lock,
    title: 'Confidential Handling',
    description: 'Your client data and case information are protected with enterprise-grade security and strict confidentiality protocols.',
    stat: '100%',
    statLabel: 'Secure',
  },
  {
    icon: Users,
    title: 'Experienced Team',
    description: 'Our legal professionals bring decades of combined experience across diverse practice areas and legal specializations.',
    stat: '15+',
    statLabel: 'Years Experience',
  },
  {
    icon: CheckCircle,
    title: 'Detail-Oriented Workflow',
    description: 'Every document undergoes rigorous quality checks and review processes to ensure accuracy and compliance.',
    stat: '99.5%',
    statLabel: 'Accuracy Rate',
  },
  {
    icon: TrendingUp,
    title: 'Scalable Support',
    description: 'Whether you need help with a single case or ongoing support, our services scale to match your workload.',
    stat: 'Unlimited',
    statLabel: 'Capacity',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export const WhyChooseUsSection = () => {
  return (
    <section
      id="why-us"
      className="py-24 md:py-32 bg-[#0a192f] relative overflow-hidden"
      data-testid="why-us-section"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D93025]/10 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
            Why Choose Us
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white mt-4 mb-6">
            The Redline Advantage
          </h2>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto">
            Partner with a firm that understands the demands of modern legal practice 
            and delivers excellence at every turn.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="feature-grid"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={itemVariants}
              className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-8 hover:bg-white/10 transition-all duration-300 group"
              data-testid={`why-us-feature-${index}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-[#D93025]/20 transition-colors">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <div className="text-right">
                  <p className="font-heading text-2xl font-semibold text-[#D93025]">
                    {feature.stat}
                  </p>
                  <p className="text-xs text-white/50 uppercase tracking-wide">
                    {feature.statLabel}
                  </p>
                </div>
              </div>
              <h3 className="font-heading text-xl font-medium text-white mb-3">
                {feature.title}
              </h3>
              <p className="text-sm text-white/60 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
